/**
 * Redistribution de panneaux dans une nouvelle forme de layout (template).
 *
 * Probleme resolu : appliquer un template de disposition (ex: "3 colonnes")
 * NE DOIT PAS jeter les panneaux actifs de l'utilisateur. Avant, le picker
 * appelait `tpl.build([])` (liste vide) → chaque cellule recevait un
 * placeholder "empty". On veut au contraire preserver les vues actives et ne
 * mettre des placeholders que dans les cellules surnumeraires.
 *
 * Strategie :
 *   1. Collecter les TabGroups "remplis" du layout courant (≥1 panel non
 *      placeholder), en ordre DFS — chacun = une "vue active" a preserver.
 *   2. Construire le squelette du template (feuilles = placeholders).
 *   3. Distribuer : feuille i ← source i (PanelInstance complets preserves :
 *      panelId / viewId / params / title / activeIndex). Les feuilles sans
 *      source gardent leur placeholder. Le surplus de sources (template avec
 *      moins de cellules que de panneaux remplis) est fusionne comme onglets
 *      supplementaires dans une cellule (1ere ou derniere selon `overflow`).
 */
import { cleanupLayout } from "./normalize";
import { mapTree, visit } from "./traverse";
import {
  EMPTY_PANEL_ID,
  type Layout,
  type PanelInstance,
  type TabGroup,
} from "./types";

/** Un group "rempli" extrait du layout courant, prêt à être replacé. */
export interface FillableGroup {
  /** Panneaux non-placeholder, dans l'ordre. */
  panels: PanelInstance[];
  /** Index actif recalcule relativement a `panels`. */
  activeIndex: number;
}

/**
 * Collecte les TabGroups contenant au moins un panel non-placeholder, en
 * ordre DFS. Les placeholders "empty" sont retires de chaque group ; un group
 * qui n'a QUE des placeholders (ou est vide) est ignore — ce n'est pas une
 * vue active a preserver.
 */
export function collectFillableGroups(layout: Layout): FillableGroup[] {
  const out: FillableGroup[] = [];
  visit(layout.root, (n) => {
    if (n.kind !== "tabs") return;
    const realPanels = n.panels.filter((p) => p.panelId !== EMPTY_PANEL_ID);
    if (realPanels.length === 0) return;
    // Recalcule activeIndex apres avoir filtre les placeholders : on retrouve
    // le panel actif par id, fallback sur 0 si l'actif etait un placeholder.
    const activeId = n.panels[n.activeIndex]?.id;
    const activeIndex = Math.max(
      0,
      realPanels.findIndex((p) => p.id === activeId)
    );
    out.push({ panels: realPanels, activeIndex });
  });
  return out;
}

/** Ids des TabGroups feuilles du layout, en ordre DFS. */
function leafGroupIdsInOrder(layout: Layout): string[] {
  const ids: string[] = [];
  visit(layout.root, (n) => {
    if (n.kind === "tabs") ids.push(n.id);
  });
  return ids;
}

/**
 * Distribue `sources` dans les feuilles de `skeleton`.
 *
 *   - feuille i ← source i (instances completes preservees)
 *   - feuilles sans source → gardent leur placeholder (rendu = EmptyPanel)
 *   - surplus de sources (plus de sources que de feuilles) → fusionne comme
 *     onglets supplementaires dans la 1ere (`overflow="first"`) ou la derniere
 *     (`overflow="last"`) feuille, sans rien perdre.
 *
 * Les ids des feuilles du squelette sont conserves ; les instances sources
 * gardent leurs propres ids (uniques car chacune n'est utilisee qu'une fois).
 */
export function distributeIntoSkeleton(
  skeleton: Layout,
  sources: FillableGroup[],
  overflow: "first" | "last" = "first"
): Layout {
  const leafIds = leafGroupIdsInOrder(skeleton);
  const n = leafIds.length;
  if (n === 0 || sources.length === 0) return skeleton;

  const overflowPos = overflow === "first" ? 0 : n - 1;
  const assign = new Map<string, FillableGroup>();

  for (let i = 0; i < sources.length; i++) {
    const src = sources[i]!;
    const leafId = leafIds[i < n ? i : overflowPos]!;
    const existing = assign.get(leafId);
    if (!existing) {
      assign.set(leafId, {
        panels: [...src.panels],
        activeIndex: src.activeIndex,
      });
    } else {
      // Fusion overflow : on append les panneaux, on garde l'actif de la
      // cellule d'origine (celle qui "possede" spatialement la feuille).
      assign.set(leafId, {
        panels: [...existing.panels, ...src.panels],
        activeIndex: existing.activeIndex,
      });
    }
  }

  const newRoot = mapTree(skeleton.root, (node) => {
    if (node.kind !== "tabs") return node;
    const a = assign.get(node.id);
    if (!a) return node; // pas de source → garde le placeholder
    const updated: TabGroup = {
      ...node,
      panels: a.panels,
      activeIndex: Math.min(a.activeIndex, a.panels.length - 1),
    };
    return updated;
  });

  return cleanupLayout({ ...skeleton, root: newRoot });
}
